import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, HostListener, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { Router } from '@angular/router';

import { Animations } from 'src/app/animations/animations';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/analytics-event';
import { AnalyticEventsLabels } from 'src/app/analytics/analytics-events-labels';
import { EventActionType } from 'src/app/analytics/event-action.enum';
import { ChatService } from 'src/app/chat/chat.service';
import { KnowledgeBaseService } from 'src/app/services/knowledge-base.service';
import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { RolePermissionsHelper } from 'src/app/auth/role-permmissions-helper';
import { environment } from 'src/environments/environment';
import { RoleEnum } from '../models/permission-role.enum';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { FeatureFlags } from 'src/app/api/models/profile/profile-feature-flags.enum';
import { MenuItemId } from '../app-sidenav-content/menu-item.enum';
import { ElasticSidenavService } from '../app-elastic-sidenav/app-elastic-sidenav.service';
import { NavIconsSpriteService } from './nav-icons-sprite.service';

/**
 * New unified sidenav shell (Navigation Restructure + Unified Assessments).
 * Rendered instead of `app-elastic-sidenav` when `DevNewSideNav` is on; the old nav
 * is untouched when the flag is off. Reuses `ElasticSidenavService` (shrink/collapse
 * state) and the `--sidenav-width` CSS vars so dependent page layouts need no change.
 * The old nav is deleted in a later cleanup phase (see old-nav-removal-plan.md).
 */
@Component({
    standalone: false,
    selector: 'app-nav',
    templateUrl: './app-nav.component.html',
    styleUrls: ['./app-nav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [Animations.collapse, Animations.pimple],
})
export class AppNavComponent implements OnInit {
    @Input()
    role: RoleEnum;

    @Input()
    showWarningBanner: boolean;

    /** Pinned collapsed state (drives the page content offset via ElasticSidenavService). */
    shrink = false;

    /** Transient hover state — expands the collapsed rail as an OVERLAY (content stays put). */
    hovered = false;

    /** Nav looks collapsed only while pinned-collapsed AND not hovered. */
    @HostBinding('class.shrink')
    get visuallyCollapsed(): boolean {
        return this.shrink && !this.hovered;
    }

    /** Hovering a collapsed rail expands the nav as an overlay over the page (rounded + shadow). */
    @HostBinding('class.overlay')
    get isOverlay(): boolean {
        return this.shrink && this.hovered;
    }

    readonly routesManager = RoutesManager;
    readonly analyticEventsConsts = AnalyticEventsLabels;
    readonly assetsUrl = environment.assets.url;

    whatsNewPermittedRoles: RoleEnum[] = [];

    readonly tpProps = { placement: 'right' } as const;

    constructor(
        private router: Router,
        private intercom: ChatService,
        private analytics: AnalyticsService,
        private cdr: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        private knowledgeBaseService: KnowledgeBaseService,
        private elasticSidenavService: ElasticSidenavService,
        private navIconsSpriteService: NavIconsSpriteService,
        private featureFlagQuery: FeatureFlagQuery,
    ) {}

    @HostListener('mouseenter')
    onMouseEnter(): void {
        // Only the collapsed rail reacts to hover — it expands as an overlay (no reflow).
        if (this.shrink && !this.hovered) {
            this.hovered = true;
            this.cdr.markForCheck();
        }
    }

    @HostListener('mouseleave')
    onMouseLeave(): void {
        if (this.hovered) {
            this.hovered = false;
            this.cdr.markForCheck();
        }
    }

    ngOnInit(): void {
        this.navIconsSpriteService.injectSprite();
        this.initPermissions();
        this.elasticSidenavService.shrink$.pipe(
            tap(shrink => this.shrink = shrink),
            tap(() => this.cdr.markForCheck()),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();

        // The bell asks the rail to collapse before opening its panel, so the panel
        // anchors to the settled (collapsed) bell position rather than the hover-expanded one.
        this.elasticSidenavService.collapseHover$.pipe(
            tap(() => this.dropHover()),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    private dropHover(): void {
        if (this.hovered) {
            this.hovered = false;
            this.cdr.markForCheck();
        }
    }

    toggle(): void {
        // Pin/unpin: flips the persisted collapsed state (reflows the page content).
        this.hovered = false;
        this.elasticSidenavService.toggle();
    }

    openChat(): void {
        this.logAnalyticsEvent('Sidebar - chat with us');
        this.intercom.show();
    }

    onHelpClick(): void {
        this.logAnalyticsEvent(AnalyticEventsLabels.SIDEBAR_help_center);
        this.knowledgeBaseService.navigate('getting-started-with-your-data-inventory');
    }

    goToTasks(): void {
        this.router.navigate([RoutesManager.tasks]);
    }

    logAnalyticsEvent(eventCategory: string): void {
        this.analytics.track({
            eventCategory,
            eventAction: EventActionType.Click,
        } as AnalyticsEvent);
    }

    private initPermissions(): void {
        this.whatsNewPermittedRoles = RolePermissionsHelper.getPerrmitedRolesByMenuId(MenuItemId.WhatsNew);
        // DSR tasks (DevDsrCollaboratorTasks): the Reviewer role is the DSR collaborator and gets What's new as well
        if (this.featureFlagQuery.getFlag(FeatureFlags.DevDsrCollaboratorTasks) && !this.whatsNewPermittedRoles.includes(RoleEnum.Reviewer)) {
            this.whatsNewPermittedRoles = [...this.whatsNewPermittedRoles, RoleEnum.Reviewer];
        }
    }
}
